import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { validatePromoCode } from "../../api";

/**
 * Promo code state for checkout (own direct/transfer only).
 * Call applyPromo({ baseAmount, effectiveCounts }) when the user applies a code.
 */
export function useCheckoutPromo({
  isPartner,
  isRoundTrip,
  isTransfer,
  trip,
  fromCity,
  toCity,
  passengerKey,
  paymentMethod,
}) {
  const { t } = useTranslation();
  const [promoInput, setPromoInput] = useState("");
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoError, setPromoError] = useState("");
  const [promoLoading, setPromoLoading] = useState(false);

  useEffect(() => {
    setAppliedPromo(null);
    setPromoError("");
  }, [passengerKey, paymentMethod]);

  const promoDiscount = appliedPromo?.promo_discount_uah
    ? Number(appliedPromo.promo_discount_uah)
    : 0;

  const handleApplyPromo = useCallback(
    async ({ baseAmount, effectiveCounts }) => {
      const code = promoInput.trim();
      if (!code || isPartner || isRoundTrip) return;
      setPromoLoading(true);
      setPromoError("");
      try {
        const payload = isTransfer
          ? { promo_code: code, subtotal_uah: baseAmount }
          : {
              promo_code: code,
              trip_id: trip.id,
              from_city: fromCity,
              to_city: toCity,
              adults: effectiveCounts.adults,
              children: effectiveCounts.children,
              payment_method: paymentMethod,
            };
        const result = await validatePromoCode(payload);
        const promoAmount = Number(result.promo_discount_uah) || 0;
        const campaignAmount = Number(result.campaign_discount_uah) || 0;
        if (promoAmount <= 0) {
          setAppliedPromo(null);
          setPromoError(
            campaignAmount > 0
              ? t("checkout.promoLosesToCampaign")
              : t("checkout.promoInvalid")
          );
          return;
        }
        setAppliedPromo(result);
        setPromoInput(result.promo_code || code);
      } catch (err) {
        setAppliedPromo(null);
        const detail =
          err?.response?.data?.detail ||
          err?.response?.data?.error ||
          t("checkout.promoInvalid");
        setPromoError(detail);
      } finally {
        setPromoLoading(false);
      }
    },
    [
      promoInput,
      isPartner,
      isRoundTrip,
      isTransfer,
      trip,
      fromCity,
      toCity,
      paymentMethod,
      t,
    ]
  );

  const handleClearPromo = () => {
    setAppliedPromo(null);
    setPromoError("");
    setPromoInput("");
  };

  return {
    promoInput,
    setPromoInput,
    appliedPromo,
    promoError,
    promoLoading,
    promoDiscount,
    handleApplyPromo,
    handleClearPromo,
  };
}
